import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, Download, Lock, Save, ShieldCheck } from "lucide-react";
import AppHeader from "@/components/AppHeader";
import AppSidebar from "@/components/AppSidebar";
import { STATUS_CLASS } from "@/components/supervision/AssistantWorkspace";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

const fmtUnit = (value, unit) => (unit === "meetings" ? `${value} meeting${Number(value) === 1 ? "" : "s"}` : unit ? `${value} ${unit}` : String(value));
const LICENSE_CLASS = { Verified: "status-active", "Pending Verification": "status-pending", Unverified: "status-inactive", Expired: "status-inactive", "No license on file": "status-neutral" };
const ENTRY_STATUS_CLASS = { Confirmed: "status-active", "Awaiting confirmation": "status-pending", Disputed: "status-inactive" };
const longDate = (value) => (value ? new Date(`${value}T00:00:00`).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : "");

function RulesEditor({ canEdit, onNotice, onError }) {
  const [payload, setPayload] = useState(null);
  const load = useCallback(async () => {
    try {
      setPayload((await api.get("/supervision/rules")).data);
    } catch (requestError) {
      onError(formatApiError(requestError));
    }
  }, [onError]);
  useEffect(() => {
    load();
  }, [load]);
  if (!payload) return null;
  const { rules, labels } = payload;
  const setValue = (section, key, value, ratioKey) => {
    const next = JSON.parse(JSON.stringify(rules));
    if (ratioKey) next.ratios[ratioKey][key] = value; else next[section][key] = value;
    setPayload({ ...payload, rules: next });
  };
  async function save() {
    try {
      setPayload((await api.put("/supervision/rules", { rules })).data);
      onNotice("Supervision requirement rules saved.");
    } catch (requestError) {
      onError(formatApiError(requestError));
    }
  }
  const sections = ["all", "DC_OT", "DC_SLP", "MD_OT", "MD_SLP"];
  return (
    <details className="supervision-history rules-editor" data-testid="supervision-rules-editor">
      <summary>Requirement rules (TAG Clinical Supervision Guidebook){payload.updated_at ? ` · last edited ${payload.updated_at.slice(0, 10)} by ${payload.updated_by_name}` : " · defaults"}</summary>
      <div className="rules-grid">
        {sections.map((section) => (
          <div key={section} className="rules-section">
            <h4>{section === "all" ? "All pairings" : section.replace("_", " ")}</h4>
            {Object.entries(rules[section]).map(([key, value]) => (
              <label key={key}>
                <span>{labels[section][key]}</span>
                {typeof value === "boolean" ? <input checked={value} data-testid={`rule-${section}-${key}`} disabled={!canEdit} type="checkbox" onChange={(e) => setValue(section, key, e.target.checked)} /> : <input data-testid={`rule-${section}-${key}`} disabled={!canEdit} step="0.5" type="number" value={value} onChange={(e) => setValue(section, key, Number(e.target.value))} />}
              </label>
            ))}
          </div>
        ))}
        <div className="rules-section">
          <h4>Ratio limits</h4>
          {Object.entries(rules.ratios).flatMap(([ratioKey, values]) => Object.entries(values).map(([key, value]) => (
            <label key={`${ratioKey}-${key}`}><span>{labels.ratios[ratioKey][key]}</span><input data-testid={`rule-ratios-${ratioKey}-${key}`} disabled={!canEdit} type="number" value={value} onChange={(e) => setValue("ratios", key, Number(e.target.value), ratioKey)} /></label>
          )))}
        </div>
      </div>
      {canEdit ? <button className="button button-primary" data-testid="save-rules-button" type="button" onClick={save}><Save size={14} /> Save rules</button> : <p className="pairing-meta"><Lock size={12} /> Only the System Administrator can edit rules.</p>}
    </details>
  );
}

function BackupCoverageAudit() {
  const [rows, setRows] = useState(null);
  const [failed, setFailed] = useState("");
  useEffect(() => {
    api.get("/supervision/pairings/backup-coverage").then((r) => setRows(r.data)).catch((e) => setFailed(formatApiError(e)));
  }, []);
  return (
    <details className="supervision-history rules-editor" data-testid="backup-coverage-audit">
      <summary>Backup supervisor coverage{rows ? ` · ${rows.length} active pairing${rows.length === 1 ? "" : "s"} without verified backup coverage` : ""}</summary>
      <p className="pairing-meta">Active pairings with no backup supervisor, or whose backup's license is not verified on the Credentials page. A backup is recommended, never required.</p>
      {failed && <p className="form-message error-message" data-testid="backup-audit-error">Could not load the coverage list: {failed}</p>}
      <ul className="backup-audit-list">
        {(rows || []).map((row) => (
          <li key={row.id} data-testid={`backup-audit-${row.id}`}>
            <strong>{row.supervisor_name} → {row.assistant_name}</strong>
            <span className="status-chip status-pending backup-warning-chip" data-testid={`backup-audit-warning-${row.id}`}>{row.warning}</span>
            <span>Backup: {row.backup_supervisor_name || "None"} · {row.jurisdiction}{row.discipline ? ` · ${row.discipline}` : ""} · {row.backup_license_detail}</span>
          </li>
        ))}
        {!rows && !failed && <li className="list-loading" data-testid="backup-audit-loading">Loading…</li>}
        {rows && !rows.length && <li data-testid="backup-audit-empty">Every active pairing has a backup supervisor with a verified license.</li>}
      </ul>
    </details>
  );
}

function ActivationReadyDrafts({ onNotice, onError, onActivated }) {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState("");
  const load = useCallback(async () => {
    try {
      setRows((await api.get("/supervision/pairings/activation-ready")).data);
    } catch (requestError) {
      onError(formatApiError(requestError));
    }
  }, [onError]);
  useEffect(() => {
    load();
  }, [load]);
  async function activate(row) {
    setBusy(row.id);
    try {
      const saved = (await api.post(`/supervision/pairings/${row.id}/activate`)).data;
      onNotice(`Activated ${saved.supervisor_name} → ${saved.assistant_name} by ${saved.activated_by_name} on ${String(saved.activated_at).slice(0, 10)}${saved.backup_warning ? ` · ${saved.backup_warning}` : ""}.`);
      await load();
      onActivated();
    } catch (requestError) {
      onError(formatApiError(requestError));
    } finally {
      setBusy("");
    }
  }
  if (!rows || !rows.length) return null;
  return (
    <section className="supervision-history rules-editor activation-ready" data-testid="activation-ready-drafts">
      <h3>Drafts ready to activate · {rows.length}</h3>
      <ul className="backup-audit-list">
        {rows.map((row) => (
          <li key={row.id} data-testid={`activation-ready-${row.id}`}>
            <strong>{row.supervisor_name} → {row.assistant_name}</strong>
            <span>{row.jurisdiction} {row.discipline} · from {row.start_date} · Backup: {row.backup_supervisor_name || "None"}</span>
            <button className="button button-primary" data-testid={`activate-ready-${row.id}`} disabled={busy === row.id} type="button" onClick={() => activate(row)}>{busy === row.id ? "Activating…" : "Activate"}</button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function LicenseChip({ license, testId }) {
  if (!license) return <span className="pairing-meta">—</span>;
  return <span className={`status-chip ${LICENSE_CLASS[license.status] || "status-neutral"}`} data-testid={testId} title={license.label}>{license.status}</span>;
}

function EntryDetails({ pairId, month }) {
  const [detail, setDetail] = useState(null);
  const [failed, setFailed] = useState("");
  useEffect(() => {
    api.get(`/supervision/verification/${pairId}/${month}`).then((r) => setDetail(r.data)).catch((e) => setFailed(formatApiError(e)));
  }, [pairId, month]);
  if (failed) return <p className="form-message error-message">{failed}</p>;
  if (!detail) return <p className="pairing-meta list-loading">Loading entries…</p>;
  const entries = detail.entries || [];
  return (
    <div>
      <h4>Entries ({entries.length})</h4>
      {!entries.length && <p className="pairing-meta" data-testid={`entries-none-${pairId}`}>No supervision logged.</p>}
      {entries.length > 0 && (
        <table className="supervision-totals" data-testid={`admin-entries-${pairId}`}>
          <thead><tr><th>Session date</th><th>Entered</th><th>Min</th><th>Type</th><th>School</th><th>Students</th><th>Covered</th><th>By</th><th>Status</th></tr></thead>
          <tbody>
            {entries.map((item) => (
              <tr key={item.id} data-testid={`admin-entry-${item.id}`}>
                <td>{item.note_date}</td>
                <td data-testid={`admin-entry-entered-${item.id}`}>{item.late_entry ? <span className="flag-text" title="Entered after the date it describes">{item.entered_on} (late)</span> : item.entered_on}</td>
                <td>{item.minutes}</td>
                <td>{item.contact_type}</td>
                <td>{item.site_name || "—"}</td>
                <td>{item.caseload_reviewed ? "Caseload reviewed" : (item.client_initials || []).join(", ") || "—"}</td>
                <td>{item.note}</td>
                <td>{item.entered_by_name}</td>
                <td><span className={`status-chip ${ENTRY_STATUS_CLASS[item.status] || ""}`}>{item.status}</span>{item.status === "Confirmed" && item.confirmed_by_name ? <><br /><small className="pairing-meta">by {item.confirmed_by_name}</small></> : null}{item.status === "Disputed" && item.dispute_comment ? <><br /><small className="flag-text">{item.dispute_comment}</small></> : null}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {detail.attestation && <p className="pairing-meta" data-testid={`attestation-detail-${pairId}`}>Attestation signed {detail.attestation.signed_on || detail.attestation.signed_at?.slice(0, 10)}{detail.attestation.late ? " (after the deadline)" : ""} · planned {detail.attestation.sessions_planned ? "yes" : "no"} · observed {detail.attestation.directly_observed ? "yes" : "no"} · co-signed {detail.attestation.notes_cosigned ? "yes" : "no"}{detail.attestation.concerns ? ` · concerns: ${detail.attestation.concerns}` : ""}</p>}
    </div>
  );
}

function VerificationRow({ row, canFinalize, onDecision }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const unmet = row.requirements.filter((r) => !r.met);
  const overdue = row.per_client.filter((c) => c.overdue);
  const canRelease = row.status === "Ready to verify" || row.status === "Held";
  return (
    <>
      <tr data-testid={`verification-row-${row.pair_id}`} className={row.reassignment_review ? "verification-flagged" : ""}>
        <td>
          <strong>{row.assistant_name}</strong><br /><small>{row.assistant_title} · <LicenseChip license={row.assistant_license} testId={`assistant-license-${row.pair_id}`} /></small>
          {row.reassignment_review && <><br /><small className="flag-text"><AlertTriangle size={11} /> Reassignment review</small></>}
        </td>
        <td data-testid={`verification-sites-${row.pair_id}`}>{row.sites.length ? row.sites.join(", ") : "No placement on file"}</td>
        <td data-testid={`verification-supervisor-${row.pair_id}`}>
          <strong>{row.supervisor_name}</strong><br /><small>{row.supervisor_title} · {row.jurisdiction} · <LicenseChip license={row.supervisor_license} testId={`supervisor-license-${row.pair_id}`} /></small>
          {row.supervisor_license?.status !== "Verified" && <><br /><small className="flag-text">{row.supervisor_license?.label}</small></>}
        </td>
        <td data-testid={`verification-backup-${row.pair_id}`}>{row.backup_label}{row.backup_license && <><br /><small><LicenseChip license={row.backup_license} testId={`backup-license-${row.pair_id}`} /></small></>}</td>
        <td data-testid={`verification-minutes-${row.pair_id}`}>
          <strong>{row.logged_label}</strong>{row.required_minutes ? <><br /><small>of {row.required_label} required</small></> : <><br /><small>{row.required_label}</small></>}
          {row.awaiting_entries > 0 && <><br /><small className="flag-text">{row.awaiting_entries} awaiting confirmation ({row.unconfirmed_minutes}m not counted)</small></>}
          {unmet.length > 0 && row.log_present && <><br /><small className="flag-text">{unmet.length} requirement{unmet.length === 1 ? "" : "s"} unmet</small></>}
          {overdue.length > 0 && <><br /><small className="flag-text">{overdue.length} client{overdue.length === 1 ? "" : "s"} overdue</small></>}
        </td>
        <td data-testid={`verification-attestation-${row.pair_id}`}>{row.attestation_present ? `Signed ${row.attestation_signed_at?.slice(0, 10)}${row.attestation_late ? " · late" : ""}` : "Not submitted"}</td>
        <td><span className={`status-chip ${STATUS_CLASS[row.status] || ""}`} data-testid={`verification-status-${row.pair_id}`}>{row.status}</span></td>
        <td data-testid={`verification-decision-${row.pair_id}`}>
          <span className={row.pay.state === "pending" ? "pairing-meta" : ""}>{row.pay.label}</span>
          {row.pay.state === "pending" && <><br /><small className="pairing-meta">Supervision time only · regular hourly pay is never held</small></>}
          {row.pay.note && <><br /><small className="pairing-meta">{row.pay.note}</small></>}
        </td>
        <td>
          <button className="provider-profile-link" data-testid={`toggle-verification-${row.pair_id}`} type="button" onClick={() => setOpen(!open)}>{open ? "Hide" : "Details"}</button>
          {canFinalize ? (
            <div className="verification-actions">
              <input data-testid={`decision-note-${row.pair_id}`} placeholder="Note" value={note} onChange={(e) => setNote(e.target.value)} />
              <button className="button button-primary" data-testid={`release-pay-${row.pair_id}`} disabled={!canRelease} title={canRelease ? "" : "Release opens once every entry is confirmed, the attestation is signed and all requirements are met"} type="button" onClick={() => onDecision(row, "Release", note)}>Release</button>
              <button className="button button-secondary" data-testid={`hold-pay-${row.pair_id}`} disabled={row.status === "Held"} type="button" onClick={() => onDecision(row, "Hold", note)}>Hold</button>
            </div>
          ) : <small className="pairing-meta"><Lock size={11} /> Released by the System Administrator</small>}
        </td>
      </tr>
      {open && (
        <tr data-testid={`verification-details-${row.pair_id}`}>
          <td colSpan={9}>
            <div className="verification-details">
              <div>
                <h4>Requirements vs actual (confirmed entries only)</h4>
                <ul>{row.requirements.map((req) => <li key={req.key} data-testid={`req-${row.pair_id}-${row.month}-${req.key}`} className={req.met ? "met" : "unmet"}>{req.label}: <strong>{fmtUnit(req.actual, req.unit)}</strong> / {fmtUnit(req.required, req.unit)}{req.basis ? <small className="pairing-meta"> · {req.basis}</small> : null}</li>)}</ul>
                <h4 style={{ marginTop: 10 }}>Per-client items</h4>
                {row.per_client.length ? <ul>{row.per_client.map((c) => <li key={c.initials} className={c.overdue ? "unmet" : "met"}>{c.initials}: last {c.last_supervised_on || "never"} · next due {c.next_due} ({c.due_basis}){c.overdue ? " · OVERDUE" : ""}</li>)}</ul> : <p className="pairing-meta">No per-client deadlines for this jurisdiction/discipline.</p>}
                <h4 style={{ marginTop: 10 }}>Licenses (Credentials page)</h4>
                <ul>
                  <li className={row.supervisor_license.ok ? "met" : "unmet"}>Supervisor {row.supervisor_name}: {row.supervisor_license.label}</li>
                  <li className={row.assistant_license.ok ? "met" : "unmet"}>Assistant {row.assistant_name}: {row.assistant_license.label}</li>
                  {row.backup_license && <li className={row.backup_license.ok ? "met" : "unmet"}>Backup {row.backup_supervisor_name}: {row.backup_license.label}</li>}
                </ul>
                {row.concerns && <p><em>Assistant concerns:</em> {row.concerns}</p>}
              </div>
              <EntryDetails pairId={row.pair_id} month={row.month} />
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default function SupervisionVerificationPage() {
  const { user } = useAuth();
  const [month, setMonth] = useState("");
  const [calendar, setCalendar] = useState(null);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState("");
  const canFinalize = user?.role === "system_administrator";
  const canActivateDrafts = ["system_administrator", "hr"].includes(user?.role);

  useEffect(() => {
    api.get("/supervision/calendar").then((r) => { setCalendar(r.data); const wanted = new URLSearchParams(window.location.search).get("month"); setMonth(wanted && /^\d{4}-\d{2}$/.test(wanted) && wanted >= (r.data.go_live_month || "2026-10") ? wanted : r.data.default_month); }).catch((e) => { setError(formatApiError(e)); setLoading(false); });
  }, []);

  const load = useCallback(async () => {
    if (!month) return;
    try {
      setRows((await api.get(`/supervision/verification?month=${month}`)).data);
      setError("");
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => {
    load();
  }, [load]);

  async function decide(row, decision, note) {
    try {
      await api.post(`/supervision/verification/${row.pair_id}/${row.month}/decision`, { decision, note });
      setNotice(`${decision === "Release" ? "Released" : "Held"} ${row.logged_label.toLowerCase()} of ${row.month_label} supervision time for ${row.assistant_name}${decision === "Release" ? ` · payable ${longDate(row.pay_date)}` : ""}. Regular hourly pay is not affected.`);
      setError("");
      await load();
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }

  const counts = rows.reduce((acc, row) => ({ ...acc, [row.status]: (acc[row.status] || 0) + 1 }), {});
  const first = rows[0];
  const beforeGoLive = calendar && month && month < calendar.go_live_month;

  return (
    <div className="app-shell" data-testid="supervision-verification-shell">
      <AppSidebar activeItem="Supervision verification" />
      <div className="app-content">
        <AppHeader />
        <main className="dashboard-page" data-testid="supervision-verification-page">
          <section className="page-intro">
            <div>
              <p className="eyebrow">COMPLIANCE</p>
              <h1>Supervision verification{first ? ` · ${first.month_label}` : ""}</h1>
              <p className="page-subtitle" data-testid="verification-subtitle">
                One row per active pairing. {first ? `Logs and attestations due ${first.deadline_label} · verify and release by ${first.release_by_label} · released supervision time paid ${first.pay_date_label}.` : "Logs and attestations are due 11:59 p.m. ET on the 2nd of the following month; the System Administrator verifies and releases by the 4th; released supervision time is paid on the 7th."} Only entries confirmed by the other person count. Holds apply to supervision time only — never regular hourly pay.
              </p>
            </div>
            <div className="verification-toolbar">
              <label>Month<input data-testid="verification-month-input" min={calendar?.go_live_month || "2026-10"} type="month" value={month} onChange={(e) => setMonth(e.target.value)} /></label>
              <a className="button button-secondary" data-testid="verification-export-csv" href={`${process.env.REACT_APP_BACKEND_URL}/api/supervision/verification/export.csv?month=${month}`} rel="noreferrer" target="_blank"><Download size={14} /> CSV</a>
            </div>
          </section>
          {error && <p className="form-message error-message" data-testid="verification-error">{error}</p>}
          {notice && <p className="form-message success-message" data-testid="verification-notice">{notice}</p>}
          <section className="provider-summary-grid" data-testid="verification-summary">
            {["Open", "Awaiting confirmation", "Awaiting attestation", "Ready to verify", "Incomplete", "Verified & released", "Held"].map((status) => <button key={status} type="button"><ShieldCheck size={18} /><strong data-testid={`verification-count-${status.replace(/[^a-z]/gi, "-")}`}>{counts[status] || 0}</strong><span>{status}</span></button>)}
          </section>
          <section className="requests-table-panel verification-table-panel">
            <table className="requests-table verification-table" data-testid="verification-table">
              <thead><tr><th>Assistant</th><th>Site(s)</th><th>Supervisor</th><th>Backup</th><th>Required vs. logged</th><th>Attestation</th><th>Status</th><th>Pay</th><th /></tr></thead>
              <tbody>
                {rows.map((row) => <VerificationRow key={row.pair_id} row={row} canFinalize={canFinalize} onDecision={decide} />)}
                {loading && <tr><td colSpan={9} className="provider-empty-state list-loading" data-testid="verification-loading">Loading…</td></tr>}
                {!loading && !error && !rows.length && <tr><td colSpan={9} className="provider-empty-state" data-testid="verification-empty">{beforeGoLive ? "Supervision tracking started October 1, 2026." : `No active pairings for ${month}.`}</td></tr>}
              </tbody>
            </table>
          </section>
          <RulesEditor canEdit={canFinalize} onNotice={setNotice} onError={setError} />
          {canActivateDrafts && <ActivationReadyDrafts onNotice={setNotice} onError={setError} onActivated={load} />}
          {canFinalize && <BackupCoverageAudit />}
        </main>
      </div>
    </div>
  );
}
